import React, { useState } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";
import { TeamRosterModal } from "../modals/RosterModal";
import AddTeamModal from "../modals/AddTeamModal";
import { InlineNotice } from "../../../utils/InlineNotice";
import { useInlineNotice } from "../../../hooks/useInlineNotice";
import { InlineConfirm } from "../../../utils/InlineConfirm";

const TeamsPanel = () => {
  const { adminId, teams, setTeams, captains, loadData } = useAdmin();

  const [editingTeamName, setEditingTeamName] = useState({}); // { [id]: draft }
  const [teamLogoUploading, setTeamLogoUploading] = useState({});
  const [viewingRoster, setViewingRoster] = useState(null);
  const [rosterPlayers, setRosterPlayers] = useState([]);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const { notice, showError, showSuccess, clear } = useInlineNotice();

  const loadRoster = async (teamId) => {
    const { data } = await supabase
      .from("players")
      .select("*")
      .eq("team_id", teamId)
      .order("jersey_number", { ascending: true });
    setRosterPlayers(data || []);
  };

  const openRoster = async (team) => {
    setViewingRoster(team);
    await loadRoster(team.id);
  };

  const saveTeamName = async (teamId) => {
    const name = editingTeamName[teamId];
    if (!name?.trim()) return;
    try {
      await supabase.functions.invoke("admin-actions", {
        body: { action: "editTeamName", adminId, payload: { teamId, name } },
      });
      setTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, name: name.trim() } : t)),
      );
      setEditingTeamName((prev) => {
        const n = { ...prev };
        delete n[teamId];
        return n;
      });
    } catch (err) {
      showError(`Error renaming team: ${err.message}`);
    }
  };

  const uploadTeamLogo = async (teamId, file) => {
    setTeamLogoUploading((prev) => ({ ...prev, [teamId]: true }));
    const ext = file.name.split(".").pop();
    const fileName = `team-${teamId}-${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("team-logos")
      .upload(fileName, file);
    if (uploadError) {
      showError(`Upload failed: ${uploadError.message}`);
      setTeamLogoUploading((prev) => ({ ...prev, [teamId]: false }));
      return;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from("team-logos").getPublicUrl(fileName);

    try {
      await supabase.functions.invoke("admin-actions", {
        body: JSON.stringify({
          action: "updateTeamLogo",
          adminId,
          payload: { teamId, logoUrl: publicUrl },
        }),
        headers: { "Content-Type": "application/json" },
      });
      setTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, logo_url: publicUrl } : t)),
      );
    } catch (err) {
      showError(`Error saving logo: ${err.message}`);
    }
    setTeamLogoUploading((prev) => ({ ...prev, [teamId]: false }));
  };

  const sendWaiverReminder = async (team) => {
    try {
      await supabase.functions.invoke("admin-actions", {
        body: {
          action: "sendWaiverReminder",
          adminId,
          payload: { teamId: team.id },
        },
      });
      showSuccess(`Waiver reminder sent to ${team.name}'s captain.`);
    } catch (err) {
      showError("Error sending waiver reminder.");
    }
  };

  const archiveTeam = async (teamId) => {
    try {
      await supabase.functions.invoke("admin-actions", {
        body: { action: "deleteTeam", adminId, payload: { teamId } },
      });
      loadData();
    } catch (err) {
      showError(`Error archiving team: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Teams & Captains
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage team rosters, captains, and settings
          </p>
        </div>
        <button
          onClick={() => setShowAddTeamModal(true)}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700
                     text-white px-5 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2
                     transition-all shadow-md hover:shadow-lg whitespace-nowrap"
        >
          <span className="text-lg">+</span>
          <span>Add New Team</span>
        </button>
      </div>

      <InlineNotice notice={notice} onDismiss={clear} />

      {/* Teams Grid */}
      <div className="grid gap-4">
        {teams.map((team) => {
          const captain = captains.find((c) => c.id === team.captain_id);
          const isEditingName = team.id in editingTeamName;
          const isUploading = !!teamLogoUploading[team.id];

          return (
            <div
              key={team.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                         rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden"
            >
              {/* Mobile & Desktop Layout */}
              <div className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {/* Logo Section */}
                  <div className="flex-shrink-0 flex flex-col items-center lg:items-start">
                    <div className="relative group w-20 h-20 lg:w-24 lg:h-24">
                      {team.logo_url ? (
                        <img
                          src={team.logo_url}
                          alt={team.name}
                          className="w-full h-full rounded-lg object-cover ring-2 ring-gray-200 dark:ring-gray-700"
                        />
                      ) : (
                        <div
                          className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200
                                        dark:from-gray-700 dark:to-gray-800 rounded-lg
                                        flex items-center justify-center text-3xl lg:text-4xl
                                        ring-2 ring-gray-200 dark:ring-gray-700"
                        >
                          🏀
                        </div>
                      )}
                      <label
                        htmlFor={`logo-upload-${team.id}`}
                        className="absolute inset-0 rounded-lg bg-black/60 text-white text-xs font-bold
                                   flex items-center justify-center cursor-pointer
                                   opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        {isUploading ? "⏳" : "📷 Upload"}
                      </label>
                      <input
                        id={`logo-upload-${team.id}`}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) =>
                          e.target.files[0] &&
                          uploadTeamLogo(team.id, e.target.files[0])
                        }
                      />
                    </div>
                    <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-2 hidden lg:block">
                      {isUploading ? "Uploading…" : "Hover to change"}
                    </p>
                  </div>

                  {/* Team Info Section */}
                  <div className="flex-1 min-w-0 space-y-3">
                    {/* Team Name */}
                    {isEditingName ? (
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <input
                          type="text"
                          value={editingTeamName[team.id]}
                          onChange={(e) =>
                            setEditingTeamName((prev) => ({
                              ...prev,
                              [team.id]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveTeamName(team.id);
                            if (e.key === "Escape")
                              setEditingTeamName((prev) => {
                                const n = { ...prev };
                                delete n[team.id];
                                return n;
                              });
                          }}
                          className="flex-1 border border-orange-300 dark:border-orange-600 px-3 py-2 rounded-lg
                                     text-lg font-bold bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                     focus:outline-none focus:ring-2 focus:ring-orange-500"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveTeamName(team.id)}
                            className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white px-4 py-2
                                       rounded-lg text-sm font-semibold transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={() =>
                              setEditingTeamName((prev) => {
                                const n = { ...prev };
                                delete n[team.id];
                                return n;
                              })
                            }
                            className="flex-1 sm:flex-none bg-gray-400 hover:bg-gray-500 dark:bg-gray-600
                                       dark:hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm
                                       font-semibold transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                          {team.name}
                        </h3>
                        <button
                          onClick={() =>
                            setEditingTeamName((prev) => ({
                              ...prev,
                              [team.id]: team.name,
                            }))
                          }
                          className="text-gray-400 hover:text-orange-500 dark:hover:text-orange-400
                                     transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Rename team"
                        >
                          ✏️
                        </button>
                      </div>
                    )}

                    {/* Record Badge */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <div
                        className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700/50
                                      px-3 py-1.5 rounded-lg"
                      >
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Record
                        </span>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {team.wins}–{team.losses}
                        </span>
                      </div>
                    </div>

                    {/* Captain Info */}
                    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                      {captain ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Captain
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {captain.name}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 break-all">
                            {captain.email}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-amber-500 dark:text-amber-400">
                            ⚠
                          </span>
                          <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                            No captain assigned
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions Section - Stacks on mobile, side-by-side on desktop */}
                  <div className="flex flex-col gap-2 lg:w-48 lg:flex-shrink-0">
                    <button
                      onClick={() => openRoster(team)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg
                                 text-sm font-semibold transition-colors shadow-sm hover:shadow w-full"
                    >
                      👥 View Roster
                    </button>
                    <InlineConfirm
                      triggerLabel="📧 Waiver Reminder"
                      triggerClassName="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2.5 rounded-lg
                                        text-sm font-semibold transition-colors shadow-sm hover:shadow w-full"
                      message={`Send waiver reminder to ${team.name}'s captain?`}
                      confirmLabel="Send"
                      confirmClassName="px-4 py-2.5 text-sm font-semibold bg-yellow-600 hover:bg-yellow-700
                                        text-white rounded-lg transition-colors shadow-sm"
                      onConfirm={() => sendWaiverReminder(team)}
                    />
                    <InlineConfirm
                      triggerLabel="🗄️ Archive Team"
                      triggerClassName="bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg
                                        text-sm font-semibold transition-colors shadow-sm hover:shadow w-full"
                      message={`Archive team "${team.name}"?\n\n• Marks team as archived\n• Removes players from standings\n• Cancels future games\n\nCannot be undone easily.`}
                      confirmLabel="Archive"
                      onConfirm={() => archiveTeam(team.id)}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {viewingRoster && (
        <TeamRosterModal
          team={viewingRoster}
          players={rosterPlayers}
          onClose={() => setViewingRoster(null)}
          onRefresh={() => loadRoster(viewingRoster.id)}
        />
      )}

      {showAddTeamModal && (
        <AddTeamModal
          onClose={() => setShowAddTeamModal(false)}
          onCreated={() => {}}
        />
      )}
    </div>
  );
};

export default TeamsPanel;
